import { useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatDate(iso) {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    timeZone: 'America/New_York',
  });
}

// Date column added at the left per the user's spec, since results here are
// NOT segregated by date - one flat list across every cached SPX day.
const COLUMNS = [
  { key: 'trade_date', label: 'Date', get: (t) => t.trade_date },
  { key: 'entry_at', label: 'Entry Time', get: (t) => t.entry_at },
  { key: 'type', label: 'Type', get: (t) => t.type },
  { key: 'strike', label: 'Strike', get: (t) => t.strike },
  { key: 'entry_price', label: 'Entry', get: (t) => t.entry_price },
  { key: 'sl_price', label: 'SL', get: (t) => t.sl_price },
  { key: 'ts_stop', label: 'TS', get: (t) => t.ts_stop },
  { key: 'high_after', label: 'H from entry', get: (t) => t.high_after },
  { key: 'low_after', label: 'L from entry', get: (t) => t.low_after },
  { key: 'pnl_pct', label: '%', get: (t) => t.pnl_pct },
  { key: 'pnl', label: 'Status', get: (t) => t.pnl },
];

// Historical replay of the Last30Bot rule set (backend: handlers/
// last30_history.go) against the ~3 months of cached SPX bar data - a
// read-only simulation, completely separate from the live recorder above
// it (Last30Widget) so it can never affect real recorded trades. One flat,
// un-dated-filtered list; the Date column is what lets days be told apart.
export default function Last30HistoryWidget({ onSelect }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'entry_at', dir: 1 });
  const [selectedIdx, setSelectedIdx] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/last30-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (mounted) { setData(json); setError(null); }
      } catch (err) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const trades = [...(data?.trades || [])].sort((a, b) => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    const va = col.get(a), vb = col.get(b);
    if (va < vb) return -sort.dir;
    if (va > vb) return sort.dir;
    return 0;
  });

  const headerClick = (key) => {
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Backtest (SPX-Last 30 Min - History)
        </span>
        {data && (
          <>
            <span className="text-xs font-mono text-gray-400">
              {data.wins}/{data.closed} wins over {data.days} days
            </span>
            <span className="text-xs font-mono font-semibold"
              style={{ color: data.total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
              {data.total_pnl >= 0 ? '+' : ''}${data.total_pnl.toFixed(0)}
            </span>
          </>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Same rule set as the live recorder above, replayed against the ~3 months of already-cached SPX bar
        data (never calls Alpaca, never touches the live table) - entries/exits fill at bar close since
        cached bars carry no real bid/ask. Flat list across all days; sort any column, click a row to chart it.
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}
      {loading && !data && <div className="text-gray-500 text-sm mb-3">Simulating...</div>}

      <div className="overflow-x-auto" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead className="sticky top-0" style={{ backgroundColor: '#111827' }}>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {COLUMNS.map((c) => (
                <th key={c.key}
                  onClick={() => headerClick(c.key)}
                  className={`py-2 pr-3 font-medium cursor-pointer select-none hover:text-gray-300 ${c.key === 'trade_date' || c.key === 'type' ? 'text-left' : 'text-right'}`}>
                  {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 && !loading ? (
              <tr><td colSpan={COLUMNS.length} className="py-4 text-center text-gray-600">
                No qualifying trades in the cached range
              </td></tr>
            ) : trades.map((t, i) => {
              const pnlColor = t.pnl > 0 ? '#22c55e' : t.pnl < 0 ? '#ef4444' : '#9ca3af';
              return (
                <tr key={`${t.symbol}-${t.entry_at}`}
                  onClick={() => { setSelectedIdx(i); onSelect?.({ symbol: t.symbol, date: t.trade_date }); }}
                  className="border-b hover:bg-gray-800/50 cursor-pointer"
                  style={{ borderColor: '#1f2937', backgroundColor: selectedIdx === i ? '#1f293780' : undefined }}>
                  <td className="py-1.5 pr-3 text-gray-300">{formatDate(t.trade_date)}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.entry_at)}</td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: (t.type === 'C' ? '#22c55e' : '#ef4444') + '22', color: t.type === 'C' ? '#22c55e' : '#ef4444' }}>
                      {t.type === 'C' ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono">
                    <a href={optionTradingViewUrl(t.symbol)} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()} title="Open on TradingView"
                      style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {t.strike % 1 === 0 ? t.strike.toFixed(0) : t.strike.toFixed(1)}
                    </a>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.entry_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${t.sl_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono"
                    style={{ color: t.ts_stop > 0 ? '#22c55e' : '#4b5563' }}>
                    {t.ts_stop > 0 ? `$${t.ts_stop.toFixed(2)}` : '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.high_after.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.low_after.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: pnlColor }}>
                    {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(0)}%
                  </td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: pnlColor + '22', color: pnlColor }}
                      title={`exit: ${t.exit_reason} @ $${t.exit_price.toFixed(2)} ${formatTime(t.exit_at)}`}>
                      {t.pnl > 0 ? 'Success' : t.pnl < 0 ? 'Failed' : 'Flat'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
